import type { Language } from "@heredajo/schema";
import { type JSX, useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LanguageSelector } from "./components/LanguageSelector.js";
import { UserMenu } from "./components/UserMenu.js";
import { hasSessionCookie } from "./lib/auth.js";
import {
  type ReadingTab,
  loadPreferredLang,
  loadPreferredTab,
  savePreferredLang,
  savePreferredTab,
} from "./lib/i18n.js";
import { t } from "./lib/strings.js";
import { useMe } from "./lib/useMe.js";
import { About } from "./routes/About.js";
import { AdminPageEditor } from "./routes/AdminPageEditor.js";
import { DiaryList } from "./routes/DiaryList.js";
import { DiaryReader } from "./routes/DiaryReader.js";
import { Login } from "./routes/Login.js";

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  if (!hasSessionCookie()) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_to=${returnTo}`} replace />;
  }
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation();
  const { me, loading } = useMe();
  if (!hasSessionCookie()) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_to=${returnTo}`} replace />;
  }
  if (loading) return <div className="max-w-6xl mx-auto px-4 py-8 text-ink-muted">…</div>;
  if (!me?.admin) return <Navigate to="/" replace />;
  return children;
}

const HTML_LANG: Record<Language, string> = { en: "en", de: "de", fr: "fr", eo: "eo" };

export function App(): JSX.Element {
  const [lang, setLang] = useState<Language>(() => loadPreferredLang());
  const [tab, setTab] = useState<ReadingTab>(() => loadPreferredTab());
  const s = t(lang);

  useEffect(() => {
    savePreferredLang(lang);
    document.documentElement.lang = HTML_LANG[lang];
  }, [lang]);

  useEffect(() => {
    savePreferredTab(tab);
  }, [tab]);

  return (
    <BrowserRouter>
      <div className="min-h-screen flex flex-col">
        <header className="bg-ink text-parchment-100 border-b border-parchment-300/20">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <Link to="/" className="font-serif text-2xl tracking-tight hover:text-parchment-300">
              Heredajo
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/about" className="text-sm opacity-80 hover:opacity-100">
                {s.about}
              </Link>
              <LanguageSelector value={lang} onChange={setLang} label={s.translationLabel} />
              <UserMenu lang={lang} />
            </div>
          </div>
        </header>

        <main className="flex-1">
          <Routes>
            <Route
              path="/"
              element={
                <RequireAuth>
                  <DiaryList lang={lang} />
                </RequireAuth>
              }
            />
            <Route
              path="/diary/:slug"
              element={
                <RequireAuth>
                  <DiaryReader lang={lang} tab={tab} onTabChange={setTab} />
                </RequireAuth>
              }
            />
            <Route
              path="/diary/:slug/page/:pageNumber"
              element={
                <RequireAuth>
                  <DiaryReader lang={lang} tab={tab} onTabChange={setTab} />
                </RequireAuth>
              }
            />
            <Route
              path="/about"
              element={
                <RequireAuth>
                  <About lang={lang} />
                </RequireAuth>
              }
            />
            <Route path="/login" element={<Login lang={lang} />} />
            <Route
              path="/admin/diary/:slug/page/:pageNumber"
              element={
                <RequireAdmin>
                  <AdminPageEditor lang={lang} />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/diary/:slug"
              element={
                <RequireAdmin>
                  <AdminPageEditor lang={lang} />
                </RequireAdmin>
              }
            />
          </Routes>
        </main>

        <footer className="border-t border-parchment-300/30 bg-parchment-100 text-ink-muted text-xs py-3">
          <div className="max-w-6xl mx-auto px-4">
            {s.attribution} {s.attributionDisclaimer}
          </div>
        </footer>
      </div>
    </BrowserRouter>
  );
}
